// Pure grid-topology helpers for cell-pick / token-spawn / location ops.
// No React, no Three.js, no mutable game state. Callers (GameBoard)
// pass in iterables of meshes + a cellAt resolver. Same pattern used
// by scope.js.
//
// Three exports:
//   cellsWithSites(meshes, cellAt)
//     → Set<'col,row'> of every cell currently occupied by a Site card.
//   bfsStepReachable(start, maxSteps, totalCols, totalRows, sitedCells)
//     → Set<'col,row'> of cells reachable within maxSteps cardinal hops.
//       Sorcery's "step" measure is path-based through sites: every
//       cell on the path past the source must contain a site, both to
//       count as a valid target AND to bridge further traversal. The
//       source cell itself is always included as step 0. "up to N
//       steps away" includes here. Empty (void) cells break the chain
//       and are never added to the result.
//   sampleCellGeometry(grid, col, row)
//     → { tl, tr, bl, br } world-space corner points for a cell, or
//       null if (col,row) is out of grid bounds. Used by halo
//       projection and token-spawn anchor math.

import { computeGridPositions, getGridPoint } from '../../grid';

export function cellsWithSites(meshes, cellAt) {
  const result = new Set();
  for (const [, mesh] of meshes) {
    const card = mesh?.userData?.cardInstance;
    if (!card?.isSite) continue;
    const cell = cellAt(mesh.position.x, mesh.position.z);
    if (cell) result.add(`${cell.col},${cell.row}`);
  }
  return result;
}

// Like cellsWithSites, but only the sites the given controller owns
// (ownership = the site card's `rotated` matches `controllerRotated`).
// Monument placement reads this: a Monument is conjured atop one of
// YOUR sites, never a void cell or an enemy site.
export function ownedSiteCells(meshes, cellAt, controllerRotated) {
  const result = new Set();
  for (const [, mesh] of meshes) {
    const card = mesh?.userData?.cardInstance;
    if (!card?.isSite) continue;
    if (!!card.rotated !== !!controllerRotated) continue;
    const cell = cellAt(mesh.position.x, mesh.position.z);
    if (cell) result.add(`${cell.col},${cell.row}`);
  }
  return result;
}

export function bfsStepReachable(start, maxSteps, totalCols, totalRows, sitedCells) {
  const out = new Set();
  if (!start) return out;
  const startKey = `${start.col},${start.row}`;

  // The source cell is step 0. Always a valid "target a location"
  // even when its own site state is unknown (the source unit is
  // standing there, which is what makes it a location at all).
  out.add(startKey);

  const visited = new Set([startKey]);
  const queue = [{ col: start.col, row: start.row, dist: 0 }];
  while (queue.length > 0) {
    const { col, row, dist } = queue.shift();
    if (dist >= maxSteps) continue;
    for (const [dc, dr] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
      const nc = col + dc;
      const nr = row + dr;
      if (nc < 0 || nr < 0 || nc >= totalCols || nr >= totalRows) continue;
      const nKey = `${nc},${nr}`;
      if (visited.has(nKey)) continue;
      visited.add(nKey);
      // A void cell terminates the path BEFORE being added. It's
      // not a valid target and it can't bridge further traversal.
      if (!sitedCells.has(nKey)) continue;
      out.add(nKey);
      queue.push({ col: nc, row: nr, dist: dist + 1 });
    }
  }
  return out;
}

export function sampleCellGeometry(grid, col, row) {
  if (!grid) return null;
  const { colPositions, rowPositions } = computeGridPositions(grid);
  if (col < 0 || row < 0) return null;
  if (col >= colPositions.length - 1 || row >= rowPositions.length - 1) return null;
  const tl = getGridPoint(grid, colPositions[col], rowPositions[row]);
  const tr = getGridPoint(grid, colPositions[col + 1], rowPositions[row]);
  const bl = getGridPoint(grid, colPositions[col], rowPositions[row + 1]);
  const br = getGridPoint(grid, colPositions[col + 1], rowPositions[row + 1]);
  return { tl, tr, bl, br };
}

// The cell whose centre is closest to a world point, for points that fall
// OUTSIDE the grid (an off-table token drop). Inside the grid the exact cell
// wins. Cell-bound things must always land on a cell: a token minted with no
// cell never enters MatchState and rests one layer under every site stack.
export function nearestCellAtWorld(grid, x, z) {
  if (!grid || !Number.isFinite(x) || !Number.isFinite(z)) return null;
  const { colPositions, rowPositions } = computeGridPositions(grid);
  let best = null;
  let bestD = Infinity;
  for (let col = 0; col < colPositions.length - 1; col += 1) {
    for (let row = 0; row < rowPositions.length - 1; row += 1) {
      const g = sampleCellGeometry(grid, col, row);
      if (!g) continue;
      const cx = (g.tl.x + g.tr.x + g.bl.x + g.br.x) / 4;
      const cz = (g.tl.z + g.tr.z + g.bl.z + g.br.z) / 4;
      const d = (cx - x) ** 2 + (cz - z) ** 2;
      if (d < bestD) {
        bestD = d;
        // Same shape getCellAt returns, so getCellNumber can label the snap.
        best = { col, row, x: cx, z: cz, totalCols: colPositions.length - 1, totalRows: rowPositions.length - 1 };
      }
    }
  }
  return best;
}
